import React, { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { CustomFieldRow } from "@/model/interface/Contact";
import { apiErrorMessage } from "@/util/apiError";

interface CustomFieldsEditorProps {
  fields?: Record<string, string>;
  onSave: (fields: Record<string, string>) => Promise<unknown>;
}

const EMPTY_ROW: CustomFieldRow = { key: "", value: "" };

export default function CustomFieldsEditor({ fields, onSave }: CustomFieldsEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [rows, setRows] = useState<CustomFieldRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const entries = Object.entries(fields ?? {});

  function startEditing() {
    setRows(entries.length > 0 ? entries.map(([key, value]) => ({ key, value })) : [EMPTY_ROW]);
    setIsEditing(true);
  }

  function updateRow(index: number, changes: Partial<CustomFieldRow>) {
    setRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...changes } : row)));
  }

  function removeRow(index: number) {
    setRows((current) => current.filter((_row, rowIndex) => rowIndex !== index));
  }

  function addRow() {
    setRows((current) => [...current, EMPTY_ROW]);
  }

  async function save() {
    const savedFields = Object.fromEntries(
      rows
        .map((row) => [row.key.trim(), row.value.trim()] as const)
        .filter(([key, value]) => key !== "" && value !== ""),
    );
    setIsSaving(true);
    try {
      await onSave(savedFields);
      setIsEditing(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not save custom fields"));
    } finally {
      setIsSaving(false);
    }
  }

  if (isEditing) {
    return (
      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              value={row.key}
              onChange={(event) => updateRow(index, { key: event.target.value })}
              placeholder="Field"
              aria-label="Field name"
              className="h-8 w-2/5"
            />
            <Input
              value={row.value}
              onChange={(event) => updateRow(index, { value: event.target.value })}
              placeholder="Value"
              aria-label="Field value"
              className="h-8 flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              title="Remove field"
              onClick={() => removeRow(index)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={addRow}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add field
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={isSaving}
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" className="h-7 text-xs" disabled={isSaving} onClick={() => void save()}>
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-2">
      {entries.length > 0 ? (
        <dl className="min-w-0 flex-1 space-y-1">
          {entries.map(([key, value]) => (
            <div key={key} className="flex gap-2 text-sm">
              <dt className="w-2/5 shrink-0 truncate text-muted-foreground">{key}</dt>
              <dd className="min-w-0 flex-1 break-words">{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-xs text-muted-foreground">No custom fields</p>
      )}
      <Button type="button" variant="ghost" size="sm" className="h-6 px-2" title="Edit custom fields" onClick={startEditing}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
